import React, { useEffect, useState } from "react";
import { FiTrash2, FiPlus, FiMinus, FiShoppingBag } from "react-icons/fi";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl, imgurl } from "../baseurl";

import { useCart } from '../context/CartContext';

function Cart() {
  const customerId = localStorage.getItem("customerId");
  const [cartItems, setCartItems] = useState([]);
  const [summary, setSummary] = useState({ total_items: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);

  // ✅ Destructure fetchCartCount so we can trigger a global refresh
  const { fetchCartCount } = useCart();
   
  const fetchCart = async () => {
    try {
      const res = await axios.get(`${baseurl}getActiveCartByCustomerId/${customerId}`);
      
      if (res.data.success && res.data.items?.length > 0) {
        setCartItems(res.data.items);
        setSummary(res.data.summary);
      } else {
        setCartItems([]);
        setSummary({ total_items: 0, total_amount: 0 });
      }

      // ✅ Trigger the global context update
      // This ensures the Topbar badge updates if you delete items or change quantities
      fetchCartCount();

    } catch (err) {
      console.error("❌ Error fetching cart:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [customerId]);

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 1500,
    timerProgressBar: true,
  });

  const updateQuantity = async (cart_item_id, action) => {
    try {
      const res = await axios.post(`${baseurl}updateCartItemQuantity`, {
        cart_item_id,
        customerId,
        action,
      });
      if (res.data.success) {
        await fetchCart();
        Toast.fire({ icon: "success", title: res.data.message || "Cart updated" });
        fetchCartCount();
      } else {
        Toast.fire({ icon: "warning", title: res.data.error || "Update failed" });
      }
    } catch (err) {
      Toast.fire({ icon: "error", title: "Unable to update quantity" });
    }
  };

  const removeItem = async (cart_item_id) => {
    try {
      const result = await Swal.fire({
        title: "Remove item?",
        text: "Are you sure you want to remove this product?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ff4757",
        confirmButtonText: "Yes, remove",
      });
      if (!result.isConfirmed) return;
      await axios.delete(`${baseurl}removeCartItem/${cart_item_id}/${customerId}`);
      await fetchCart();
      Toast.fire({ icon: "success", title: "Item removed" });
    } catch (err) {
      Toast.fire({ icon: "error", title: "Failed to remove item" });
    }
  };

  const totalPrice = cartItems.reduce((sum, item) => sum + parseFloat(item.product_data.price || 0) * item.quantity, 0);
  const totalMRP = cartItems.reduce((sum, item) => sum + parseFloat(item.product_data.mrp || 0) * item.quantity, 0);
  const totalDiscount = totalMRP - totalPrice;
  const deliveryCharges = 0;

  if (loading) {
    return (
      <div className="cart-loader">
        <div className="spinner-grow text-primary" role="status"></div>
        <p>Syncing your bag...</p>
      </div>
    );
  }

  return (
    <div className="cart-wrapper">
      <div className="container py-lg-5 py-3">
        <div className="d-flex align-items-center mb-4">
          <FiShoppingBag className="me-2 fs-3 text-primary" />
          <h2 className="fw-bold m-0">Shopping Bag ({cartItems.length})</h2>
        </div>

        {cartItems.length === 0 ? (
          <div className="empty-cart-state text-center py-5 shadow-sm rounded bg-white">
            <div className="empty-icon mb-3">🛒</div>
            <h4>Your cart is empty</h4>
            <p className="text-muted">Looks like you haven't added anything to your cart yet.</p>
            <Link to="/" className="btn btn-primary px-4 py-2">Start Shopping</Link>
          </div>
        ) : (
          <div className="row g-4">
            {/* Left Column: Items */}
            <div className="col-lg-8">
              <div className="cart-items-container">
                {cartItems.map((item) => {
                  const p = item.product_data;
                  const imageObj = p?.images?.[0] || null;
                  const imagePath = imageObj?.path?.startsWith("http") 
                    ? imageObj.path 
                    : `${imgurl.replace(/\/$/, "")}/${imageObj?.path?.replace(/^\/+/, "")}`;

                  return (
                    <div key={item.cart_item_id} className="cart-card mb-3">
                      <div className="row g-0 align-items-center p-3">
                        <div className="col-4 col-md-2">
                          <img 
                            src={imagePath} 
                            alt={p?.name} 
                            className="img-fluid rounded border shadow-sm item-image" 
                            onError={(e) => (e.target.src = "https://via.placeholder.com/150")}
                          />
                        </div>
                        <div className="col-8 col-md-6 ps-3 ps-md-4">
                          <h6 className="item-title mb-1 text-truncate">{p?.name}</h6>
                          {p?.parameters && (
                            <p className="item-params mb-1 small text-muted">
                              {Object.entries(p.parameters).map(([k, v]) => `${k}: ${v}`).join(" | ")}
                            </p>
                          )}
                          <div className="price-tag mb-2">
                            <span className="current-price fw-bold text-dark">₹{parseFloat(p?.price).toLocaleString()}</span>
                            <span className="old-price text-muted text-decoration-line-through ms-2 small">₹{parseFloat(p?.mrp).toLocaleString()}</span>
                          </div>
                          
                          {/* Mobile-only layout hides actions here, Desktop shows them */}
                          <div className="d-none d-md-flex align-items-center gap-3 mt-2">
                            <div className="qty-selector">
                              <button onClick={() => updateQuantity(item.cart_item_id, "decrease")}><FiMinus /></button>
                              <span>{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.cart_item_id, "increase")}><FiPlus /></button>
                            </div>
                            <button className="remove-link" onClick={() => removeItem(item.cart_item_id)}>
                              <FiTrash2 /> Remove
                            </button>
                          </div>
                        </div>
                        
                        {/* Mobile-only Action Row */}
                        <div className="col-12 d-md-none border-top mt-3 pt-3 d-flex justify-content-between align-items-center">
                           <div className="qty-selector">
                              <button onClick={() => updateQuantity(item.cart_item_id, "decrease")}><FiMinus /></button>
                              <span>{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.cart_item_id, "increase")}><FiPlus /></button>
                            </div>
                            <button className="remove-link" onClick={() => removeItem(item.cart_item_id)}>
                              <FiTrash2 /> Remove
                            </button>
                        </div>

                        <div className="col-md-4 d-none d-md-block text-end">
                            <h6 className="fw-bold mb-0">Total: ₹{(p.price * item.quantity).toLocaleString()}</h6>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Summary */}
            <div className="col-lg-4">
              <div className="summary-card p-4 shadow-sm position-sticky" style={{ top: "100px" }}>
                <h5 className="fw-bold border-bottom pb-3 mb-3">Order Summary</h5>
                <div className="summary-row">
                  <span>Price ({cartItems.length} Items)</span>
                  <span>₹{totalMRP.toLocaleString()}</span>
                </div>
                <div className="summary-row text-success">
                  <span>Discount</span>
                  <span>- ₹{totalDiscount.toLocaleString()}</span>
                </div>
                <div className="summary-row">
                  <span>Delivery</span>
                  <span className="text-success">{deliveryCharges === 0 ? "FREE" : `₹${deliveryCharges}`}</span>
                </div>
                <hr className="my-3" />
                <div className="summary-row total mb-4">
                  <span>Total Amount</span>
                  <span>₹{(totalPrice + deliveryCharges).toLocaleString()}</span>
                </div>
                <p className="text-success small fw-bold text-center mb-3">You will save ₹{totalDiscount.toLocaleString()} on this order</p>
                <Link to="/checkout" className="btn btn-primary btn-lg w-100 shadow-sm checkout-btn">
                  Checkout Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Sticky Footer */}
      {cartItems.length > 0 && (
        <div className="mobile-action-footer d-lg-none shadow-lg">
          <div className="d-flex justify-content-between align-items-center px-3 py-3 container">
            <div>
              <small className="text-muted d-block">Total to pay</small>
              <span className="fw-bold fs-5">₹{(totalPrice + deliveryCharges).toLocaleString()}</span>
            </div>
            <Link to="/checkout" className="btn btn-primary px-5 py-2 fw-bold rounded-pill">
              Checkout
            </Link>
          </div>
        </div>
      )}

      <style>{`
        .cart-wrapper { background-color: #f8f9fa; min-height: 100vh; padding-bottom: 100px; }
        .cart-loader { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 80vh; }
        
        .cart-card { background: #fff; border-radius: 12px; border: 1px solid #eee; transition: transform 0.2s ease; }
        .item-image { width: 100%; height: 100px; object-fit: contain; background: #fff; }
        .item-title { font-size: 1.1rem; font-weight: 600; color: #2d3436; }
        
        .qty-selector { display: flex; align-items: center; background: #f1f2f6; border-radius: 8px; padding: 4px; }
        .qty-selector button { border: none; background: #fff; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
        .qty-selector span { width: 40px; text-align: center; font-weight: bold; }
        
        .remove-link { background: none; border: none; color: #ff4757; font-size: 0.9rem; font-weight: 500; display: flex; align-items: center; gap: 5px; }
        
        .summary-card { background: #fff; border-radius: 16px; border: 1px solid #eee; }
        .summary-row { display: flex; justify-content: space-between; margin-bottom: 12px; font-size: 1rem; color: #636e72; }
        .summary-row.total { font-size: 1.3rem; color: #2d3436; font-weight: 800; }
        
        .checkout-btn { border-radius: 12px; background: #ed712f; border: none; transition: 0.3s; }
        .checkout-btn:hover { background: #000; transform: translateY(-2px); }

        .mobile-action-footer { position: fixed; bottom: 0; left: 0; right: 0; background: #fff; border-top: 1px solid #eee; z-index: 1000; }
        
        @media (max-width: 768px) {
          .cart-wrapper { padding-bottom: 120px; }
          .item-title { font-size: 1rem; }
          .item-image { height: 80px; }
          .summary-card { margin-top: 20px; border: none; box-shadow: none; background: #f8f9fa; }
        }
      `}</style>
    </div>
  );
}

export default Cart;